import React from "react";
import { Box } from "@mui/material";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";

export type ParticleShape = "square" | "triangle";

export interface CanvasParticlesProps {
  numParticles?: number;
  colors?: string[]; // will default to team primary and neutral gray
  shapes?: ParticleShape[]; // default ["square","triangle"]
  sizeRange?: [number, number]; // px
  speedRange?: [number, number]; // vertical px/sec
  swayAmplitudeRange?: [number, number]; // px
  swayPeriodRange?: [number, number]; // seconds for a full left-right sway cycle
  rotationSpeedRange?: [number, number]; // deg/sec
  opacityRange?: [number, number];
  zIndex?: number; // overlay z-index
  className?: string;
  style?: React.CSSProperties;
  fogHeight?: string;
}

type Particle = {
  x0: number; // base x around which we sway
  y: number;
  size: number;
  color: string;
  shape: ParticleShape;
  vy: number; // vertical speed px/sec
  swayAmplitude: number; // px
  swayAngularSpeed: number; // rad/sec
  swayPhase: number; // rad
  rotation: number; // deg
  rotationSpeed: number; // deg/sec
  opacity: number;
};

function randomBetween(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export function CanvasParticles({
  numParticles = 60,
  shapes = ["square", "triangle"],
  sizeRange = [4, 10],
  speedRange = [40, 90],
  swayAmplitudeRange = [6, 16],
  swayPeriodRange = [2.2, 4.2],
  rotationSpeedRange = [40, 120],
  opacityRange = [0.35, 0.8],
  colors,
  zIndex = 0,
  className,
  style,
  fogHeight = "0%",
}: CanvasParticlesProps) {
  const theme = useSSTheme();
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const frameRef = React.useRef<number | null>(null);
  const particlesRef = React.useRef<Particle[]>([]);
  const startTimeRef = React.useRef<number | null>(null);
  const sizeRef = React.useRef({ width: 0, height: 0, dpr: 1 });

  const palette = React.useMemo(() => {
    const teamPrimary = theme.mui.palette.primary.main;
    const neutral = theme.mui.palette.grey[300] || "#A1A1AA";
    return colors && colors.length > 0 ? colors : [teamPrimary, neutral];
  }, [colors, theme.mui.palette.primary.main, theme.mui.palette.grey]);

  const ensureSize = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement as HTMLElement | null;
    const width = parent?.clientWidth || 0;
    const height = parent?.clientHeight || 0;
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    if (width === 0 || height === 0) return;
    sizeRef.current = { width, height, dpr };
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
  }, []);

  const respawnParticle = React.useCallback(
    (p?: Particle): Particle => {
      const { width, height } = sizeRef.current;
      const x0 = Math.random() * width;
      const size = randomBetween(sizeRange[0], sizeRange[1]);
      const color = palette[Math.floor(Math.random() * palette.length)];
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      const vy = randomBetween(speedRange[0], speedRange[1]);
      const swayAmplitude = randomBetween(
        swayAmplitudeRange[0],
        swayAmplitudeRange[1]
      );
      const period = randomBetween(swayPeriodRange[0], swayPeriodRange[1]);
      const swayAngularSpeed = (Math.PI * 2) / period; // rad per sec
      const rotationSpeed = randomBetween(
        rotationSpeedRange[0],
        rotationSpeedRange[1]
      );
      const opacity = randomBetween(opacityRange[0], opacityRange[1]);
      const y = -randomBetween(0, height); // spawn somewhere above
      const swayPhase = Math.random() * Math.PI * 2;

      const next =
        p ??
        ({
          x0,
          y,
          size,
          color,
          shape,
          vy,
          swayAmplitude,
          swayAngularSpeed,
          swayPhase,
          rotation: Math.random() * 360,
          rotationSpeed,
          opacity,
        } as Particle);

      next.x0 = x0;
      next.y = y;
      next.size = size;
      next.color = color;
      next.shape = shape;
      next.vy = vy;
      next.swayAmplitude = swayAmplitude;
      next.swayAngularSpeed = swayAngularSpeed;
      next.swayPhase = swayPhase;
      next.rotation = Math.random() * 360;
      next.rotationSpeed = rotationSpeed;
      next.opacity = opacity;
      return next;
    },
    [
      palette,
      shapes,
      sizeRange,
      speedRange,
      swayAmplitudeRange,
      swayPeriodRange,
      rotationSpeedRange,
      opacityRange,
    ]
  );

  const initParticles = React.useCallback(() => {
    particlesRef.current = Array.from({ length: numParticles }).map(() =>
      respawnParticle()
    );
  }, [numParticles, respawnParticle]);

  const draw = React.useCallback(
    (ctx: CanvasRenderingContext2D, now: number) => {
      if (startTimeRef.current == null) startTimeRef.current = now;
      const prev = startTimeRef.current;
      const dt = Math.min(0.05, (now - prev) / 1000); // clamp big jumps
      startTimeRef.current = now;

      const { width, height, dpr } = sizeRef.current;
      ctx.clearRect(0, 0, width * dpr, height * dpr);
      ctx.save();
      ctx.scale(dpr, dpr);

      for (let i = 0; i < particlesRef.current.length; i++) {
        const p = particlesRef.current[i];
        // update
        p.y += p.vy * dt;
        p.rotation += p.rotationSpeed * dt;
        const x =
          p.x0 +
          Math.sin((now / 1000) * p.swayAngularSpeed + p.swayPhase) *
            p.swayAmplitude;

        // recycle
        if (p.y - p.size > height + 24) {
          respawnParticle(p);
          continue;
        }

        // draw
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        const s = p.size;
        if (p.shape === "square") {
          ctx.fillRect(-s / 2, -s / 2, s, s);
        } else {
          // triangle - equilateral
          const h = (Math.sqrt(3) / 2) * s;
          ctx.beginPath();
          ctx.moveTo(0, -h / 1.1);
          ctx.lineTo(-s / 2, h / 2);
          ctx.lineTo(s / 2, h / 2);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }

      // no fog overlay

      ctx.restore();
    },
    [respawnParticle]
  );

  const loop = React.useCallback(
    (now: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      draw(ctx, now);
      frameRef.current = requestAnimationFrame(loop);
    },
    [draw]
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    ensureSize();
    initParticles();
    frameRef.current = requestAnimationFrame(loop);

    const ro = new ResizeObserver(() => {
      ensureSize();
    });
    if (canvasRef.current?.parentElement)
      ro.observe(canvasRef.current.parentElement);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    numParticles,
    palette.join("|"),
    shapes.join("|"),
    sizeRange.toString(),
    speedRange.toString(),
    swayAmplitudeRange.toString(),
    swayPeriodRange.toString(),
    rotationSpeedRange.toString(),
    opacityRange.toString(),
  ]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex,
        overflow: "hidden",
      }}
      className={className}
      style={style}
    >
      {fogHeight ? (
        <span
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "100%",
            height: fogHeight,
            backgroundColor: theme.mui.palette.common.white,
            backdropFilter: "blur(2px)",
            boxShadow: `0px 0px 100px rgba(255, 255, 255, 0.8)`,
          }}
        />
      ) : null}
      <canvas ref={canvasRef} />
    </Box>
  );
}

export default CanvasParticles;
